import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getLogger } from "@/lib/logger";
const logger = getLogger();
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });
        // Log error to monitoring service
        logger.error("React Error Boundary caught error", {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });
        // Call optional error callback
        this.props.onError?.(error, errorInfo);
    }
    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };
    handleGoHome = () => {
        window.location.href = "/";
    };
    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (_jsxs("div", { className: "flex min-h-[400px] flex-col items-center justify-center p-8 text-center", children: [_jsx("div", { className: "rounded-full bg-destructive/10 p-4", children: _jsx(AlertCircle, { className: "h-8 w-8 text-destructive" }) }), _jsx("h2", { className: "mt-4 text-xl font-bold", children: "Something went wrong" }), _jsx("p", { className: "mt-2 max-w-md text-muted-foreground", children: "An unexpected error occurred. Our team has been notified." }), import.meta.env.DEV && this.state.error && (_jsxs("details", { className: "mt-4 max-w-lg text-left", children: [_jsx("summary", { className: "cursor-pointer text-sm text-muted-foreground hover:text-foreground", children: "Error details" }), _jsxs("pre", { className: "mt-2 overflow-auto rounded-lg bg-muted p-4 text-xs", children: [this.state.error.message, "\n\n", this.state.error.stack] })] })), _jsxs("div", { className: "mt-6 flex gap-3", children: [_jsxs(Button, { variant: "outline", onClick: this.handleReset, children: [_jsx(RefreshCw, { className: "mr-2 h-4 w-4" }), "Try Again"] }), _jsxs(Button, { onClick: this.handleGoHome, children: [_jsx(Home, { className: "mr-2 h-4 w-4" }), "Go Home"] })] })] }));
        }
        return this.props.children;
    }
}
// ============================================
// Page-level error boundary with React Router
// ============================================
export function PageErrorBoundary({ children }) {
    return (_jsx(ErrorBoundary, { fallback: _jsxs("div", { className: "flex min-h-[60vh] flex-col items-center justify-center p-8 text-center", children: [_jsx("div", { className: "rounded-full bg-destructive/10 p-4", children: _jsx(AlertCircle, { className: "h-12 w-12 text-destructive" }) }), _jsx("h1", { className: "mt-6 text-2xl font-bold", children: "Page Error" }), _jsx("p", { className: "mt-3 max-w-md text-muted-foreground", children: "This page encountered an error. Please try refreshing or return to the homepage." }), _jsxs("div", { className: "mt-8 flex gap-4", children: [_jsxs(Button, { variant: "outline", onClick: () => window.location.reload(), children: [_jsx(RefreshCw, { className: "mr-2 h-4 w-4" }), "Refresh Page"] }), _jsxs(Button, { onClick: () => (window.location.href = "/"), children: [_jsx(Home, { className: "mr-2 h-4 w-4" }), "Back to Home"] })] })] }), children: children }));
}
